import { useMemo, useState } from 'react';
import { Button, Modal, Select } from 'semantic-ui-react';
import Swal from 'sweetalert2';
import EventService from 'src/services/api/eventService';
import { dayInTZ, eventDisplayName, searchSelectProps } from 'src/components/helpers';
import { getGuestFullName } from './helpers';

const AssignGuestToEventModal = ({ open, guest, assignableFutureEvents, onClose, onAssigned }) => {
  const [loading, setLoading] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState('');

  const eventOptions = useMemo(
    () =>
      assignableFutureEvents.map((event) => ({
        key: event.id,
        value: event.id,
        text: `${eventDisplayName(event.eventSlugname)} — ${dayInTZ(
          event.date,
          'DD/MM/YYYY hh:mm a'
        )}`,
      })),
    [assignableFutureEvents]
  );

  const handleClose = () => {
    setSelectedEventId('');
    onClose();
  };

  const handleAssign = () => {
    const targetEvent = assignableFutureEvents.find((e) => e.id === selectedEventId);

    if (!targetEvent) {
      return Swal.fire('Selecciona un evento futuro para asignar al invitado.');
    }

    const guestName = getGuestFullName(guest);
    const eventLabel = dayInTZ(targetEvent.date, 'DD/MM/YYYY hh:mm a');

    Swal.fire({
      title: `¿Asignar a ${guestName || 'este invitado'}?`,
      text: `Evento: ${eventDisplayName(targetEvent.eventSlugname)} — ${eventLabel}`,
      showCancelButton: true,
      confirmButtonText: 'Sí, asignar',
      cancelButtonText: 'Cancelar',
    }).then((result) => {
      if (!result.value) return;

      setLoading(true);
      EventService.assignGuestFromPending({
        audGuest_id: guest.id,
        audEvent_id: targetEvent.id,
        eventSlugname: targetEvent.eventSlugname,
      })
        .then((response) => {
          if (response.data.success) {
            onAssigned(guest.id);
            handleClose();
            if (response.data.message) Swal.fire(response.data.message);
          } else if (response.data.message) {
            Swal.fire(response.data.message);
          }
        })
        .catch((error) => {
          if (error.response?.data?.message) Swal.fire(error.response.data.message);
        })
        .finally(() => setLoading(false));
    });
  };

  if (!guest) return null;

  return (
    <Modal open={open} onClose={handleClose} size="small" closeIcon>
      <Modal.Header>Asignar invitado a evento</Modal.Header>
      <Modal.Content>
        <p style={{ marginBottom: '12px' }}>
          <strong>Invitado:</strong> {getGuestFullName(guest) || '—'}
          <br />
          <strong>Evento anterior:</strong> {eventDisplayName(guest.eventSlugname)}
        </p>
        {!eventOptions.length ? (
          <p>No hay eventos futuros disponibles para asignar.</p>
        ) : (
          <Select
            placeholder="Selecciona un evento futuro"
            fluid
            {...searchSelectProps({
              value: selectedEventId,
              handler: (e, { value }) => setSelectedEventId(value),
              options: eventOptions,
            })}
          />
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button
          primary
          loading={loading}
          disabled={!selectedEventId || !eventOptions.length}
          onClick={handleAssign}
        >
          Asignar
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default AssignGuestToEventModal;
